import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'
import { CurrentConvertedCalendars } from 'utils/google_events'
import { ProviderType } from 'utils/groups'
import {
  ThirdPartyAccountByEmail,
  ThirdPartyCalendarAccounts
} from './db/Queries/ThirdPartyCalendarsQuieries'
import { connectThirdPartyToServer } from './api/group'
import Database from './db/Database'
import Logger from './Logger'

function calendarIdFromRequestUrl(url?: string) {
  if (!url) return ''

  let decoded = url
  try {
    decoded = decodeURIComponent(url)
  } catch {
    decoded = url
  }

  const marker = '/calendars/'
  const markerIndex = decoded.indexOf(marker)
  const path =
    markerIndex >= 0
      ? decoded.slice(markerIndex + marker.length)
      : decoded.replace(/^https?:\/\/[^/]+/, '')

  return path.split('/')[0]?.split('?')[0] ?? ''
}

async function googleAccountForCalendarId(calendarId: string) {
  const email =
    CurrentConvertedCalendars.find((cal) => cal.id === calendarId)
      ?.primaryCalendarEmail ||
    CurrentConvertedCalendars.find(
      (cal) => cal.primaryCalendarEmail === calendarId
    )?.primaryCalendarEmail ||
    calendarId

  const googleAccount = await ThirdPartyAccountByEmail(
    email,
    ProviderType.GOOGLE
  )
  if (googleAccount?.token) {
    return googleAccount
  }

  const fallback = await ThirdPartyAccountByEmail(email)
  if (fallback?.token) {
    return fallback
  }

  const googleAccounts = await ThirdPartyCalendarAccounts(ProviderType.GOOGLE)
  return (
    googleAccounts.find((account) => account.email === email && account.token) ??
    googleAccounts.find((account) => Boolean(account.token))
  )
}

class GoogleApi {
  instance = axios.create({
    baseURL: `https://www.googleapis.com/calendar/v3/calendars/`
  })

  constructor() {
    // Updated request interceptor for Axios 1.x
    this.instance.interceptors.request.use(
      async (req: InternalAxiosRequestConfig) => {
        const calendarId = calendarIdFromRequestUrl(req.url)
        const account = await googleAccountForCalendarId(calendarId)

        if (account?.token) {
          req.headers.set('Authorization', `Bearer ${account.token}`)
        }

        return req
      }
    )

    // Updated response interceptor for Axios 1.x
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (!error.response || error.response.status !== 401) {
          return Promise.reject(error)
        }

        // Handle 401 Unauthorized error (token expired)
        const originalRequest = error.config
        if (!originalRequest) {
          return Promise.reject(error)
        }

        const calendarId = calendarIdFromRequestUrl(originalRequest.url)
        const account = await googleAccountForCalendarId(calendarId)
        const email = account?.email

        if (email) {
          try {
            const tokenResponse = await connectThirdPartyToServer({
              email: email,
              action: 'REFRESH_TOKEN',
              type: 'Google'
            })

            Logger.blue('refresh token from google response', tokenResponse)

            const updatedAccount = await Database.write(async () => {
              return (
                await ThirdPartyAccountByEmail(email, ProviderType.GOOGLE)
              ).update((local) => {
                local.token = tokenResponse.access_token
                local._raw['tokenExpire'] = tokenResponse.expiry_date
              })
            })

            // Set the new Authorization header
            originalRequest.headers.Authorization = `Bearer ${updatedAccount.token}`

            // Retry the original request with the new token
            return axios(originalRequest)
          } catch (e) {
            return Promise.reject(e)
          }
        }

        // If we can't refresh the token, reject the promise
        return Promise.reject(error)
      }
    )
  }
}

export default new GoogleApi()
